import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { RootState } from '../store/types';

const NavigationContainer = styled.nav`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  padding: 8px 16px;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  z-index: 1000;
`;

const NavigationList = styled.ul`
  display: flex;
  justify-content: space-around;
  list-style: none;
  margin: 0;
  padding: 0;
`;

const NavigationItem = styled.li`
  flex: 1;
  text-align: center;
`;

const NavigationButton = styled.button<{ $active?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 100%;
  padding: 8px;
  background: none;
  border: none;
  color: ${props => props.$active ? '#007AFF' : '#666'};
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: #007AFF;
  }
`;

const IconWrapper = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  position: relative;
`;

const Icon = styled.span`
  font-size: 20px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
`;

const ProfileImage = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  background-color: #f0f0f0;
  display: block;
  position: absolute;
  top: 0;
  left: 0;
`;

const LoadingSpinner = styled.div`
  width: 24px;
  height: 24px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #007AFF;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

interface NavigationItem {
  label: string;
  path: string;
  icon: string;
}

interface BottomNavigationProps {
  items: NavigationItem[];
  hidden?: boolean;
}

const BottomNavigation: React.FC<BottomNavigationProps> = ({ items, hidden = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const profileImage = useSelector((state: RootState) => state.auth.user?.profileImage);
  const [imageError, setImageError] = React.useState(false);

  // Reset image error state when avatar changes
  React.useEffect(() => {
    setImageError(false);
  }, [profileImage]);

  // Add debug logging
  console.log('Navigation avatar:', profileImage);

  if (hidden) {
    return null;
  }

  return (
    <NavigationContainer>
      <NavigationList>
        {items.map((item) => (
          <NavigationItem key={item.path}>
            <NavigationButton
              onClick={() => navigate(item.path)}
              $active={location.pathname === item.path}
            >
              <IconWrapper>
                {item.label === 'Profile' ? (
                  <>
                    {profileImage && !imageError ? (
                      <ProfileImage 
                        src={profileImage} 
                        alt="Profile"
                        onError={(e) => {
                          console.error('Profile image failed to load:', profileImage);
                          setImageError(true);
                          e.currentTarget.style.display = 'none';
                        }}
                        onLoad={() => {
                          console.log('Profile image loaded successfully:', profileImage);
                          setImageError(false);
                        }}
                      />
                    ) : (
                      <Icon>{item.icon}</Icon>
                    )}
                  </>
                ) : (
                  <Icon>{item.icon}</Icon>
                )}
              </IconWrapper>
              {item.label}
            </NavigationButton>
          </NavigationItem>
        ))}
      </NavigationList>
    </NavigationContainer>
  );
};

export default BottomNavigation; 